import React, { useEffect, useMemo, useRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/South';
import KeyboardArrowUpIcon from '@mui/icons-material/North';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { useHistory } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';
import Box from '@mui/material/Box';
import { isNumber } from 'lodash';
import cx from 'classnames';
import TrashIcon from '@mui/icons-material/FolderDelete';
import FolderOffIcon from '@mui/icons-material/FolderOff';
import Checkbox from '@mui/material/Checkbox';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@mui/material';
import { useSnackbar } from 'material-ui-snackbar-provider';

import { deleteFolderById, getFolderDeleteImpact, getUserOrganization, patchMoveFolders } from '../../api';
import {
  getFoldersThunk,
  removeFoldersFromList,
  saveQueryOptions,
  updateSortOption,
  updateSortType,
  updateTriggerFolderFetch,
} from '../../store/folders/foldersSlice';
import { saveCurrentPlayingTimelineClip, updateNotes, updateQualifiers } from '../../store/videos/videoSlice';
import ShareFolderModal from '../SharingModals/MyFolderPage/ShareFolderModal';
import { switchSidebar } from '../../store/sharedStore/sharedStoreSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import ComposerFolder from '../modules/Folders/ComposerFolder';
import { PaginationFooter } from '../modules/PaginationFooter';
import { NOTIFICATION, STATUS, VIDEOFEED_TYPE } from '../../enums/ENUMS';
import { SortOptions, SortType } from '../../enums/SORT';
import { OrderSelector } from '../modules/Selector';
import LinearProgress from '../modules/LinearProgress';
import SearchInput from '../modules/SearchInput';
import Modal from '../modules/Modal';
import styles from './MyFolders.module.scss';
import deleteModalStyles from '../VideofeedsList/DeleteVideofeedModal/DeleteVideofeedModal.module.scss';
import {
  getMyFoldersPagePaginationLimit,
  getMyFoldersSortOptionAndType,
  getSortStateForMyFolders,
  setMyFoldersPagePaginationLimit,
  setMyFoldersSortOptionAndType,
  useDimensions,
} from '../../helpers';
import { PATHS, ROLES } from '../../enums';
import { ContainedButton, OutlinedButton, RolesButton } from '../modules/Buttons';
import { FOLDER_TYPE } from '../../enums/TYPES';
import { saveMyOrganization } from '../../store/user/userSlice';
import MoveButton from '../modules/MoveButton';
import FilterCollaboratorModal from './FilterCollaboratorModal';
import MoveFoldersModal from './MoveFoldersModal';
import { beginMoveUndo, rememberFolderLocations } from '../../helpers/moveUndo';
import DateSortHeader from '../modules/DateSortHeader';
import DeletedFoldersModal from '../VideofeedsList/DeletedFoldersModal';
import { useContentDragDrop } from '../modules/ContentDragDrop/useContentDragDrop';
import PinnedContentSection from '../modules/PinnedContentSection';

const MyFolders = () => {
  const dispatch = useAppDispatch();
  const notification = useSnackbar();
  const { folders, status, page, limit, sort, total, showShareModal, sortOption, triggerFolderFetch } = useAppSelector(
    (state) => state.folderStore,
  );
  const { role, currentUser } = useAppSelector((state) => state.user);
  const history = useHistory();
  const { widthD } = useDimensions();
  const [selectedType, setSelectedType] = useState<'all' | 'personal' | 'collaborative'>('all');
  const [collabIds, setCollabIds] = useState<string[]>([]);
  const [pageNum, setPageNum] = useState<number>(0);

  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (role && role.toLowerCase() === ROLES.VIEWER.toLowerCase()) {
      history.replace(PATHS.SHARED_WITH_ME.path);
    }
  }, [role]);
  useEffect(() => {
    dispatch(switchSidebar(false));
    dispatch(updateNotes([]));
    dispatch(updateQualifiers([]));
    dispatch(saveCurrentPlayingTimelineClip(null));
    const prefferedLimit = getMyFoldersPagePaginationLimit();
    if (prefferedLimit && isNumber(+prefferedLimit)) {
      dispatch(saveQueryOptions({ page: 0, limit: parseInt(prefferedLimit, 10), sort }));
    }
    setReady(true);
  }, [currentUser?.id]);

  const [search, setSearch] = useState('');
  const debouncedValue = useDebounce<string>(search, 500);

  const fetchAllFolders = (sortFromArg?: SortType, sortOpinionFromArg?: SortOptions) => {
    return dispatch(
      getFoldersThunk({
        page: pageNum,
        limit,
        search: debouncedValue,
        sort: sortFromArg ? sortFromArg : sort,
        type: VIDEOFEED_TYPE.TIMELINE,
        sortOption: sortOpinionFromArg ? sortOpinionFromArg : sortOption,
        selectedType,
        collabIds,
      }),
    );
  };

  const [fetchTrigger, setFetchTrigger] = useState(0);
  useEffect(() => {
    if (!ready) return;
    const res = getMyFoldersSortOptionAndType();
    if (res) {
      const sortAndOption = res.split('/');
      if (sortAndOption[0] && sortAndOption[1]) {
        dispatch(updateSortType(sortAndOption[0]));
        dispatch(updateSortOption(sortAndOption[1]));
        const request = fetchAllFolders(sortAndOption[0] as SortType, sortAndOption[1] as SortOptions);
        return () => request.abort();
      }
    } else if (folders) {
      const request = fetchAllFolders();
      return () => request.abort();
    }
  }, [ready, debouncedValue, pageNum, limit, sort, sortOption, fetchTrigger, triggerFolderFetch, selectedType, collabIds]);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(saveQueryOptions({ page: 0, limit, sort }));
    setPageNum(0);
    setSearch(e.target.value);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  const onRowsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(saveQueryOptions({ page: 0, limit: parseInt(e.target.value, 10), sort }));
    setPageNum(0);
    setMyFoldersPagePaginationLimit(parseInt(e.target.value, 10));
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  const onTypeChange = (type: typeof selectedType) => {
    dispatch(saveQueryOptions({ page: 0, limit, sort }));
    setPageNum(0);
    setSelectedType(type);
  };

  const onPaginate = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    dispatch(saveQueryOptions({ page: newPage, limit, sort }));
    setPageNum(newPage);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  const sortHandler = (singleValue: SortOptions, option?: { label: string; value: string; order: SortType }) => {
    const selectedSortOption = option ? (option.value as SortOptions) : singleValue;
    if (selectedSortOption === SortOptions.OWNER && !showOwnerColumn) {
      return;
    }

    let nextSort = SortType.ASC;
    if (option) {
      nextSort = option.order;
      // @ts-ignore
      setMyFoldersSortOptionAndType(option.order, option.value);
    } else if (sortOption === singleValue) {
      nextSort = sort === SortType.ASC ? SortType.DESC : SortType.ASC;
      setMyFoldersSortOptionAndType(nextSort, singleValue);
    } else {
      const sortState = JSON.parse(getSortStateForMyFolders()!);
      if (sortState && sortState[singleValue]) {
        nextSort = sortState[singleValue];
      }
      setMyFoldersSortOptionAndType(nextSort, singleValue);
    }
    dispatch(updateSortType(nextSort));
    dispatch(updateSortOption(selectedSortOption));
    dispatch(saveQueryOptions({ page: 0, limit, sort: nextSort }));
    setPageNum(0);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  const fetchDataCallback = () => {
    setFetchTrigger(Date.now());
  };

  const listRef = useRef<HTMLDivElement>(null);

  const [filterOpen, setFilterOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>([]);
  const [showDeletedFoldersModal, setShowDeletedFoldersModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkDeleteImpactLoading, setBulkDeleteImpactLoading] = useState(false);
  const [bulkDeleteImpactedComposers, setBulkDeleteImpactedComposers] = useState<string[]>([]);
  const [showMoveFoldersModal, setShowMoveFoldersModal] = useState(false);
  const [foldersToMove, setFoldersToMove] = useState<FOLDER_TYPE[]>([]);
  const [draggedFolderId, setDraggedFolderId] = useState<string | null>(null);
  const [movingFolderIds, setMovingFolderIds] = useState<string[]>([]);

  const showOwnerColumn = useMemo(
    () => folders.some((folder) => (folder?.collaborators?.length || 0) + (folder?.group_collaborators?.length || 0) > 0),
    [folders],
  );

  useEffect(() => {
    if (showOwnerColumn || sortOption !== SortOptions.OWNER) return;
    const sortState = JSON.parse(getSortStateForMyFolders() || 'null');
    const fallbackSortType = sortState?.[SortOptions.NAME] || SortType.ASC;
    dispatch(updateSortType(fallbackSortType));
    dispatch(updateSortOption(SortOptions.NAME));
    setMyFoldersSortOptionAndType(fallbackSortType, SortOptions.NAME);
    onPaginate(null, 0);
  }, [showOwnerColumn, sortOption]);

  const isMovableFolder = (folder: FOLDER_TYPE) =>
    (!folder.organization?.id ||
      String(folder.organization.id) === String(currentUser?.organization?.id)) &&
    !folder.is_collaborative;

  const moveFolderByDrag = async (folder: FOLDER_TYPE, destination: FOLDER_TYPE) => {
    if (
      String(folder.id) === String(destination.id) ||
      !isMovableFolder(folder) ||
      !isMovableFolder(destination)
    ) {
      return;
    }

    setDraggedFolderId(null);
    setMovingFolderIds((current) => [...new Set([...current, String(folder.id)])]);
    const showMoveUndo = beginMoveUndo();
    const undoMove = rememberFolderLocations([folder]);

    try {
      await patchMoveFolders(
        [folder.id],
        destination.id,
        encodeURI(`${PATHS.VIDEOFEEDS_LIST.path}?folderId=${destination.id}&parentPage=${PATHS.MY_FOLDERS.label}`),
        encodeURI(`${PATHS.SHARED_VIDEOFEEDS_LIST.path}?folderId=${destination.id}&parentPage=${PATHS.SHARED_WITH_ME.label}`),
      );
      dispatch(removeFoldersFromList([String(folder.id)]));
      showMoveUndo(`Moved to ${destination.name}`, undoMove);
    } catch (err: any) {
      notification.showMessage(err?.response?.data?.message || 'Failed to move folder');
    } finally {
      setMovingFolderIds((current) => current.filter((id) => id !== String(folder.id)));
    }
  };

  const selectedFolderIdSet = useMemo(() => new Set(selectedFolderIds), [selectedFolderIds]);
  const selectedFolders = useMemo(
    () => folders.filter((folder) => selectedFolderIdSet.has(String(folder.id))),
    [folders, selectedFolderIdSet],
  );
  const canBulkMoveFolders = selectedFolders.length > 0 && selectedFolders.every(isMovableFolder);

  const openMoveFoldersModal = (targetFolders: FOLDER_TYPE[]) => {
    if (!targetFolders.length) return;
    setFoldersToMove(targetFolders);
    setShowMoveFoldersModal(true);
  };

  const closeMoveFoldersModal = () => {
    setShowMoveFoldersModal(false);
    setFoldersToMove([]);
  };

  const clearSelection = () => {
    setSelectedFolderIds([]);
    setSelectMode(false);
    setShowBulkDeleteModal(false);
    closeMoveFoldersModal();
  };

  const toggleSelectMode = () => {
    if (selectMode) {
      clearSelection();
      return;
    }

    setSelectMode(true);
  };
  const onDeletedFoldersClick = () => {
    setShowDeletedFoldersModal(!showDeletedFoldersModal);
  };

  const toggleFolderSelection = (folder: FOLDER_TYPE) => {
    const folderId = String(folder.id);
    setSelectedFolderIds((prev) =>
      prev.includes(folderId) ? prev.filter((id) => id !== folderId) : [...prev, folderId],
    );
  };

  const removeResumeUploadByFolderId = (targetFolderId: string) => {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);

      if (key && key.startsWith('=videofeedToResume=') && key.includes(`=${targetFolderId}::`)) {
        localStorage.removeItem(key);
      }
    }
  };

  const onBulkDeleteConfirm = async () => {
    setBulkDeleteLoading(true);

    try {
      for (const folderToDelete of selectedFolders) {
        removeResumeUploadByFolderId(folderToDelete.id);
        await deleteFolderById(folderToDelete.id);
      }

      const organizationResponse = await getUserOrganization();
      dispatch(saveMyOrganization(organizationResponse.data));
      dispatch(updateTriggerFolderFetch());
      setFetchTrigger(Date.now());
      clearSelection();
    } catch (err: any) {
      notification.showMessage(err?.response?.data?.message || NOTIFICATION.NOT_DELETED);
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const openBulkDeleteModal = async () => {
    setBulkDeleteImpactLoading(true);
    setBulkDeleteImpactedComposers([]);
    setShowBulkDeleteModal(true);

    try {
      const impactedNames = new Set<string>();

      for (const folderToDelete of selectedFolders) {
        const response = await getFolderDeleteImpact(folderToDelete.id);
        const names = Array.isArray(response?.data) ? response.data : [];
        names.forEach((name: string) => {
          const normalized = String(name || '').trim();
          if (normalized) impactedNames.add(normalized);
        });
      }

      setBulkDeleteImpactedComposers([...impactedNames]);
    } catch {
      setBulkDeleteImpactedComposers([]);
    } finally {
      setBulkDeleteImpactLoading(false);
    }
  };

  useEffect(() => {
    const visibleIds = new Set(folders.map((folder) => String(folder.id)));
    setSelectedFolderIds((prev) => prev.filter((id) => visibleIds.has(id)));
  }, [folders]);

  const visibleFolders = folders.filter((folder) => !movingFolderIds.includes(String(folder.id)));
  const visibleFolderEntries = visibleFolders.map((folder, index) => ({ folder, index }));
  const pinnedFolderEntries = pageNum === 0
    ? visibleFolderEntries.filter(({ folder }) => Boolean((folder as any)?.is_pinned))
    : [];
  const regularFolderEntries = pageNum === 0
    ? visibleFolderEntries.filter(({ folder }) => !Boolean((folder as any)?.is_pinned))
    : visibleFolderEntries;

  const drag = useContentDragDrop<FOLDER_TYPE>({
    items: folders,
    contextType: 'my_folders',
    contextParentId: null,
    firstPage: pageNum === 0,
    targetType: () => 'folder',
    refresh: () => { setPageNum(0); fetchDataCallback(); },
    disabled: selectMode || status === STATUS.LOADING,
    canMove: (source, target) => isMovableFolder(source) && isMovableFolder(target),
    move: moveFolderByDrag,
    onStart: (folder) => { setDraggedFolderId(String(folder.id)); },
    onEnd: () => { setDraggedFolderId(null); },
  });

  const renderFolderRow = ({ folder, index }: { folder: FOLDER_TYPE; index: number }) => (
    <div
      key={folder.id}
      className={cx(
        styles.folderListRow,
        draggedFolderId === String(folder.id) && styles.dragging,
      )}
      {...drag.rowProps(folder)}
    >
      {drag.indicator(folder)}
      {selectMode && (
        <div className={styles.selectionCheckbox}>
          <Checkbox
            checked={selectedFolderIdSet.has(String(folder.id))}
            onChange={() => toggleFolderSelection(folder)}
            sx={{
              color: 'rgba(255,255,255,0.6)',
              '&.Mui-checked': {
                color: '#39b3ea',
              },
            }}
          />
        </div>
      )}
      <div className={styles.folderListRowContent} style={{ pointerEvents: selectMode ? 'none' : undefined }}>
        <ComposerFolder
          folderIndex={index}
          folder={folder}
          data={{ page, limit, search, sort, sortOption }}
          showOwnerColumn={showOwnerColumn}
          fetchDataCallback={fetchDataCallback}
          canMove={isMovableFolder(folder)}
          onMove={(targetFolder) => openMoveFoldersModal([targetFolder])}
          pinContextType="my_folders"
          pinContextParentId={null}
          onPinUpdated={fetchDataCallback}
        />
      </div>
    </div>
  );

  return (
    <div className={styles.myFolders}>
      <div className={styles.topBar}>
        <div className={styles.smallPath}>File Management</div>
        <div className={styles.pageTitle}> My Folders </div>
      </div>
      <div className={styles.searchWrapper}>
        <SearchInput quickFilter onChange={onSearch} className={styles.search} />
        <div className={styles.sortToolbar}>
          <OrderSelector
            title={'Sorting by'}
            selectedValue={sortOption}
            selectedOrder={sort}
            options={[
              { label: 'A-Z ↓ ', value: SortOptions.NAME, order: SortType.ASC },
              { label: 'Z-A ↑ ', value: SortOptions.NAME, order: SortType.DESC },
              ...(showOwnerColumn
                ? [
                    { label: 'Owner ↓', value: SortOptions.OWNER, order: SortType.DESC },
                    { label: 'Owner ↑', value: SortOptions.OWNER, order: SortType.ASC },
                  ]
                : []),
              { label: 'Size ↓', value: SortOptions.SIZE, order: SortType.DESC },
              { label: 'Size ↑', value: SortOptions.SIZE, order: SortType.ASC },
              { label: 'Last Viewed ↓', value: SortOptions.LAST_VIEWED, order: SortType.DESC },
              { label: 'Last Viewed ↑', value: SortOptions.LAST_VIEWED, order: SortType.ASC },
              { label: 'Last Modified ↓', value: SortOptions.UPDATED_AT, order: SortType.DESC },
              { label: 'Last Modified ↑', value: SortOptions.UPDATED_AT, order: SortType.ASC },
              { label: 'Created At ↓', value: SortOptions.CREATED_AT, order: SortType.DESC },
              { label: 'Created At ↑', value: SortOptions.CREATED_AT, order: SortType.ASC },
            ]}
            onSelect={sortHandler}
          />
        </div>

        <ContainedButton className={styles.filterCollBtn} onClick={() => setFilterOpen(true)}>
          Filter by user
        </ContainedButton>
        {currentUser?.id && (
          <FilterCollaboratorModal
            open={filterOpen}
            setOpen={setFilterOpen}
            onApply={(ids) => {
              dispatch(saveQueryOptions({ page: 0, limit, sort }));
              setPageNum(0);
              setCollabIds(ids);
            }}
            currentUserId={currentUser.id}
          />
        )}

        <div className={styles.filterByType}>
          <RolesButton
            kind={selectedType === 'all' ? 'white' : 'gray'}
            hover={selectedType !== 'all' ? 'white' : 'none'}
            onClick={() => onTypeChange('all')}
            customStyles={{ borderRadius: 'var(--ui-border-radius)' }}
          >
            <div className={styles.roleButton}>All {selectedType === 'all' && <CheckIcon sx={{ height: '12px' }} />}</div>
          </RolesButton>
          <RolesButton
            kind={selectedType === 'collaborative' ? 'blue' : 'gray'}
            hover={selectedType !== 'collaborative' ? 'blue' : 'none'}
            onClick={() => onTypeChange('collaborative')}
            customStyles={{ borderRadius: 'var(--ui-border-radius)', marginLeft: '8px' }}
          >
            <div className={styles.roleButton}>
              Collaborative {selectedType === 'collaborative' && <CheckIcon sx={{ height: '12px' }} />}
            </div>
          </RolesButton>

          <RolesButton
            kind={selectedType === 'personal' ? 'orange' : 'gray'}
            hover={selectedType !== 'personal' ? 'orange' : 'none'}
            onClick={() => onTypeChange('personal')}
            customStyles={{ borderRadius: 'var(--ui-border-radius)', marginLeft: '8px' }}
          >
            <div className={styles.roleButton}>Personal {selectedType === 'personal' && <CheckIcon sx={{ height: '12px' }} />}</div>
          </RolesButton>
        </div>

        <div className={styles.selectionToolbar}>
          {selectMode && (
            <>
              <span className={styles.selectionCount}>{selectedFolderIds.length} selected</span>
              <Tooltip title="Only folders you own can be moved. Parent-organisation folders are locked.">
                <InfoOutlinedIcon className={styles.selectionInfoIcon} />
              </Tooltip>
              {canBulkMoveFolders && (
                <button type="button" className={styles.selectionActionButton} onClick={() => openMoveFoldersModal(selectedFolders)}>
                  <MoveButton />
                  <span>Move</span>
                </button>
              )}
              <button
                type="button"
                className={styles.selectionIconButton}
                onClick={openBulkDeleteModal}
                disabled={!selectedFolderIds.length}
              >
                <TrashIcon />
              </button>
            </>
          )}

          {selectMode ? (
            <OutlinedButton
              kind="default"
              className={cx(styles.selectionToggleButton, styles.selectionToggleButtonActive)}
              onClick={toggleSelectMode}
            >
              Cancel
            </OutlinedButton>
          ) : (
            <>
              <Tooltip title="Deleted folders">
                <div className={styles.selectionIconButton} onClick={onDeletedFoldersClick}>
                  <FolderOffIcon />
                </div>
              </Tooltip>
              <ContainedButton kind="purple" className={styles.selectionToggleButton} onClick={toggleSelectMode}>
                <span className={styles.selectionToggleContent}>
                  <AddIcon className={styles.selectionToggleIcon} />
                  <span>Select</span>
                </span>
              </ContainedButton>
            </>
          )}
        </div>
      </div>
      {(status === STATUS.LOADING || drag.busy) && (
        <>
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
        </>
      )}
      {folders.length > 0 && (
        <>
          <div className={cx(styles.toolbar, selectMode && styles.toolbarSelectMode)}>
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <div className={styles.name} onClick={() => sortHandler(SortOptions.NAME)}>
                  <button type="button" className={styles.btn}>
                    Name
                  </button>
                  {sortOption === SortOptions.NAME && sort === SortType.ASC && (
                    <>
                      <KeyboardArrowDownIcon style={{ fontSize: '13px', marginLeft: '4px' }} />
                    </>
                  )}
                  {sortOption === SortOptions.NAME && sort === SortType.DESC && (
                    <>
                      <KeyboardArrowUpIcon style={{ fontSize: '13px', marginLeft: '4px' }} />
                    </>
                  )}
                </div>
              </div>
              <div className={cx(styles.rightSide, !showOwnerColumn && styles.rightSideNoOwner)}>
                {showOwnerColumn && (
                  <div className={styles.team} onClick={() => sortHandler(SortOptions.OWNER)}>
                    Owner
                    {sortOption === SortOptions.OWNER && sort === SortType.ASC && (
                      <>
                        <KeyboardArrowDownIcon style={{ fontSize: '13px', marginLeft: '4px' }} />
                      </>
                    )}
                    {sortOption === SortOptions.OWNER && sort === SortType.DESC && (
                      <>
                        <KeyboardArrowUpIcon style={{ fontSize: '13px', marginLeft: '4px' }} />
                      </>
                    )}
                  </div>
                )}
                <div className={styles.collaborators}>Collaborators</div>
                <div className={styles.date}>
                  <DateSortHeader
                    sortOption={sortOption}
                    sort={sort}
                    compact={widthD <= 1330}
                    onSelect={sortHandler}
                    arrowClassName={styles.dateArrow}
                  />
                </div>
                <div className={styles.size} onClick={() => sortHandler(SortOptions.SIZE)}>
                  {widthD > 1330 ? 'File Size' : 'Size'}
                  {sortOption === SortOptions.SIZE && sort === SortType.DESC && (
                    <>
                      <KeyboardArrowDownIcon style={{ fontSize: '13px', marginLeft: '4px' }} />
                    </>
                  )}
                  {sortOption === SortOptions.SIZE && sort === SortType.ASC && (
                    <>
                      <KeyboardArrowUpIcon style={{ fontSize: '13px', marginLeft: '4px' }} />
                    </>
                  )}
                </div>

                <div className={styles.duration}> {widthD > 1430 ? 'Upload Hours' : 'Hours'}</div>

                <div className={styles.actionBtns}>Actions</div>
              </div>
            </div>
          </div>
          <div ref={listRef} className={styles.foldersList} id={'folder_custom_scroller'}>
            {(pinnedFolderEntries.length > 0 || drag.active) && (
              <div key="pinned-content" {...drag.zoneProps(true)}>
                <PinnedContentSection count={pinnedFolderEntries.length}>
                  {pinnedFolderEntries.map(renderFolderRow)}
                  {drag.emptyHint(true, pinnedFolderEntries.length === 0)}
                </PinnedContentSection>
              </div>
            )}
            <div key="regular-content" {...drag.zoneProps(false)}>
              {regularFolderEntries.map(renderFolderRow)}
              {drag.emptyHint(false, regularFolderEntries.length === 0)}
            </div>
          </div>
          <PaginationFooter
            hideVersion
            page={pageNum}
            total={total}
            onPageChange={onPaginate}
            rowsPerPage={limit}
            onRowsPerPageChange={onRowsChange}
            pinnedCount={folders.filter((folder: any) => folder?.is_pinned).length}
          />
        </>
      )}
      {folders.length === 0 && status === STATUS.IDLE && (
        <div className={styles.noFoldersContainer}>
          <div className={styles.noFoldersText}>No Content</div>
          <div className="emptyStateHint">Add your first folder from the left sidebar!</div>
        </div>
      )}
      {showShareModal && <ShareFolderModal setFetchTrigger={setFetchTrigger} />}
      <MoveFoldersModal
        open={showMoveFoldersModal}
        folders={foldersToMove}
        onClose={closeMoveFoldersModal}
        onMoved={() => {
          setFetchTrigger(Date.now());
          dispatch(updateTriggerFolderFetch());
          clearSelection();
        }}
      />
      <Modal open={showBulkDeleteModal} onClose={() => setShowBulkDeleteModal(false)} loading={bulkDeleteLoading}>
        <div className={deleteModalStyles.modalBody}>
          <div className={deleteModalStyles.modalTitle}>
            Are you sure you want to delete {selectedFolderIds.length} folder{selectedFolderIds.length === 1 ? '' : 's'}?
          </div>
          <div className={deleteModalStyles.restoreDisclaimer}>
            Deleted folders can be restored for 30 days from Deleted folders.
          </div>
          {!bulkDeleteImpactLoading && !!bulkDeleteImpactedComposers.length && (
            <>
              <div className={deleteModalStyles.modalSubtitleSecondary}>
                Deleting these folders will also remove clips from these Online Composers. Composers that only depend on this source will be deleted:
              </div>
              <div className={deleteModalStyles.namesList}>
                {bulkDeleteImpactedComposers.map((name) => (
                  <div key={name} className={deleteModalStyles.nameItem}>
                    {name}
                  </div>
                ))}
              </div>
            </>
          )}
          <div className={deleteModalStyles.actionBtns}>
            <OutlinedButton kind="default" disabled={bulkDeleteLoading || bulkDeleteImpactLoading} onClick={onBulkDeleteConfirm}>
              Yes, I am sure
            </OutlinedButton>
            <ContainedButton kind="purple" disabled={bulkDeleteLoading || bulkDeleteImpactLoading} onClick={() => setShowBulkDeleteModal(false)}>
              No, cancel
            </ContainedButton>
          </div>
        </div>
      </Modal>
      <DeletedFoldersModal
        open={showDeletedFoldersModal}
        switchModal={onDeletedFoldersClick}
        onRestored={() => {
          dispatch(updateTriggerFolderFetch());
        }}
      />
    </div>
  );
};

export default MyFolders;
